import { useState } from "react";
import { Search, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Searchable combobox listing the Sellsy product catalog grouped by base name.
 * Used both in Aperçu (ExtractedDataPanel) and in Actions > Créer une opportunité.
 *
 * Props:
 *  - animations: array of `{ base_name, variants: [...] }`
 *  - selectedBaseNames: array of lowercase base names already picked (to disable in list)
 *  - onAdd(baseName): called when the user picks one
 *  - label: optional Label text above the trigger (default "Animations")
 *  - placeholder: trigger placeholder text
 */
const AnimationCatalogCombobox = ({
  animations,
  selectedBaseNames = [],
  onAdd,
  label = "Animations",
  placeholder = "Ajouter une animation depuis le catalogue Sellsy...",
}) => {
  const [open, setOpen] = useState(false);
  const lowerSelected = new Set(selectedBaseNames.map((s) => (s || "").toLowerCase()));

  return (
    <div>
      {label && <Label className="text-xs text-slate-600">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-8 text-sm justify-between font-normal"
            data-testid="animation-select"
          >
            <span className="text-slate-500 truncate">{placeholder}</span>
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Rechercher..." className="h-8 text-sm" />
            <CommandList>
              <CommandEmpty className="py-3 text-center text-xs text-slate-500">
                Aucune animation trouvée
              </CommandEmpty>
              <CommandGroup>
                {(animations || []).map((anim) => {
                  const alreadyPicked = lowerSelected.has(anim.base_name.toLowerCase());
                  return (
                    <CommandItem
                      key={anim.base_name}
                      value={anim.base_name}
                      onSelect={(value) => {
                        const found = (animations || []).find(
                          (a) => a.base_name.toLowerCase() === value.toLowerCase()
                        );
                        onAdd(found ? found.base_name : value);
                        setOpen(false);
                      }}
                      disabled={alreadyPicked}
                      className="text-sm"
                      data-testid={`animation-option-${anim.base_name}`}
                    >
                      <span className="truncate">{anim.base_name}</span>
                      {(anim.variants?.length || 0) > 1 && (
                        <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">
                          {anim.variants.length} variantes
                        </span>
                      )}
                      {alreadyPicked && (
                        <CheckCircle2 className="w-3 h-3 ml-2 text-emerald-500 flex-shrink-0" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AnimationCatalogCombobox;
